import { useCallback, useEffect } from 'react';

import { useCreatePurchaseMutation } from '@store/api/purchases.api';
import { CreatePurchaseRequest } from '@store/api/types';

import { useGetGuestOrUser } from '@shared/hooks/useGetGuestOrUser';

import { useChangeVolunteerTab } from './useChangeVolunteerTab';
import { CABINET_TAB_VERIFYING } from '../../constants';

import { useUser } from '@shared/hooks/useUser';
import { UserType } from '@shared/enums';
import { isApiError } from '@shared/utils/errors';
import { useGetGuest } from '@shared/hooks/useGetGuest';
import { ExtendedUser } from '@shared/types/common.types';

interface UseCreatePurchaseOptions {
  onSuccess?: () => void;
}

function useCreatePurchaseRequest() {
  const [mutation, { data, isSuccess, isLoading: isCreatePurchaseLoading }] = useCreatePurchaseMutation();
  const [getGuest, { isLoading: isGetGuestLoading }] = useGetGuest();

  async function createPurchaseRequest(
    body: CreatePurchaseRequest,
    user: ExtendedUser,
    isRetryPurchase: boolean = false,
  ) {
    try {
      /**
       * Try to create purchase
       */
      await mutation(body).unwrap();
    } catch (err) {
      /**
       * If it's retry purchase then throw error
       */
      if (isRetryPurchase) {
        throw err;
      }

      /**
       * If it's an API unauthorized error we try to make a request
       * with other guest
       */
      if (isApiError(err) && err.data.code === 401 && user.type === UserType.Guest) {
        const guest = await getGuest();

        console.log(guest);

        if (!guest) {
          throw err;
        }

        await createPurchaseRequest(body, guest, true);
      }
    }
  }

  return {
    createPurchaseRequest,

    data,
    isSuccess,
    isLoading: isGetGuestLoading || isCreatePurchaseLoading,
  };
}

export function useCreatePurchase(options: UseCreatePurchaseOptions = {}) {
  const changeVolunteerTab = useChangeVolunteerTab();

  const [user] = useUser();

  const { createPurchaseRequest, data, isSuccess, isLoading: isCreatePurchaseLoading } = useCreatePurchaseRequest();
  const [getGuestOrUser, { isLoading: isGetGuestLoading }] = useGetGuestOrUser();

  useEffect(() => {
    if (isSuccess && data && user) {
      const mutationData = data as { redirectUrl: (string | null | Location) & Location };

      if (mutationData.redirectUrl === null) {
        /**
         * purchase was successful even without payment (for example, after reissue certificate)
         * So show modal right now
         */
        options.onSuccess?.();
      } else {
        /**
         * We need to make redirect to payment in order to finish purchase
         */
        window.location = mutationData.redirectUrl;

        if (user.type !== UserType.Guest) {
          /**
           * NOTE: We need this to show user the `Verifying` tab if user returns to site back without payment.
           */
          changeVolunteerTab(CABINET_TAB_VERIFYING);
        }
      }
    }
  }, [isSuccess, data, user]);

  const createPurchase = useCallback(
    async (body: CreatePurchaseRequest) => {
      // 1. Firstly we need to get current user or guest
      const user = await getGuestOrUser();
      if (!user) {
        return;
      }

      await createPurchaseRequest(body, user);
    },
    [getGuestOrUser],
  );

  return [createPurchase, { isLoading: isCreatePurchaseLoading || isGetGuestLoading }] as const;
}
